<template>
  <transition name="modal-backdrop">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-[9999] flex items-end justify-center bg-black/60 backdrop-blur-sm"
      @click.self="closeModal"
    >
      <transition name="modal-slide">
        <div
          v-if="isOpen"
          class="w-full max-w-2xl bg-white rounded-t-3xl shadow-2xl overflow-hidden"
          style="max-height: 75vh"
        >
          <!-- Header -->
          <div
            class="sticky top-0 z-10 bg-white border-b border-[#e8d5c4] px-4 py-3 flex items-center justify-between"
          >
            <h3 class="text-base sm:text-lg font-bold text-[#5c3a1f]">
              Pilih Varian
            </h3>
            <button
              class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#faf2e7] transition-colors"
              @click="closeModal"
            >
              <svg
                class="w-5 h-5 text-[#8b6f3e]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2.5"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <!-- Scrollable Content -->
          <div
            class="overflow-y-auto px-4 py-5"
            style="max-height: calc(75vh - 140px)"
          >
            <!-- Product Info -->
            <div class="flex gap-3 mb-6 pb-5 border-b border-[#e8d5c4]">
              <div
                class="flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden bg-[#f5e6d3] border-2 border-[#e8d5c4]"
              >
                <img
                  v-if="product.image && !imageError"
                  :src="product.image"
                  :alt="product.name"
                  class="w-full h-full object-cover"
                  @error="imageError = true"
                >
                <div
                  v-else
                  class="w-full h-full flex items-center justify-center text-neutral-300"
                >
                  <svg
                    class="w-8 h-8"
                    viewBox="0 0 64 64"
                    fill="none"
                    stroke="currentColor"
                  >
                    <rect
                      x="8"
                      y="12"
                      width="48"
                      height="40"
                      rx="4"
                      stroke-width="2.5"
                    />
                    <circle
                      cx="44"
                      cy="22"
                      r="4"
                      stroke-width="2"
                    />
                  </svg>
                </div>
              </div>

              <div
                class="flex-1 flex flex-col justify-start items-start text-left"
              >
                <h4
                  class="text-sm font-bold text-[#5c3a1f] mb-1 line-clamp-2 leading-tight"
                >
                  {{ product.name }}
                </h4>
                <p
                  v-if="product.description"
                  class="text-xs text-[#8b6f3e] mb-2 line-clamp-2 leading-snug"
                >
                  {{ product.description }}
                </p>
                <p class="text-lg font-bold text-[#8b6f3e]">
                  Rp {{ formatPrice(currentPrice) }}
                </p>
              </div>
            </div>

            <!-- Variant Selection -->
            <div
              v-if="variants.length > 0"
              class="mb-6"
            >
              <label
                class="block text-sm font-bold text-[#5c3a1f] mb-3 text-left"
              >
                Pilih Varian
              </label>

              <div class="grid grid-cols-3 gap-2 sm:gap-3">
                <button
                  v-for="variant in variants"
                  :key="variant.id"
                  class="w-full px-2 py-3.5 rounded-xl font-bold text-[11px] sm:text-xs transition-all border-2 flex flex-col items-center justify-center text-center leading-tight shadow-sm min-h-[52px]"
                  :class="[
                    selectedVariant?.id === variant.id
                      ? 'bg-[#8b6f3e] text-[#f5e6d3] border-[#8b6f3e] ring-2 ring-[#8b6f3e]/20'
                      : isVariantAvailable(variant)
                        ? 'bg-[#faf2e7] text-[#5c3a1f] border-[#e8d5c4] hover:border-[#8b6f3e]'
                        : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed',
                  ]"
                  :disabled="!isVariantAvailable(variant)"
                  @click="selectVariant(variant)"
                >
                  <span>{{ variant.name }}</span>
                  <span
                    v-if="!isVariantAvailable(variant)"
                    class="text-[9px] mt-0.5"
                  >(Habis)</span>
                </button>
              </div>
            </div>

            <!-- Additional Selection -->
            <div
              v-if="hasAdditionals"
              class="mb-6"
            >
              <label
                class="block text-sm font-bold text-[#5c3a1f] mb-3 text-left"
              >
                Tambahan (Opsional)
              </label>

              <div class="space-y-2">
                <button
                  v-for="additional in additionals"
                  :key="additional.id"
                  class="w-full px-4 py-3 rounded-xl font-medium text-xs sm:text-sm transition-all border-2 flex items-center justify-between text-left shadow-sm"
                  :class="[
                    selectedAdditional?.id === additional.id
                      ? 'bg-[#8b6f3e] text-[#f5e6d3] border-[#8b6f3e] ring-2 ring-[#8b6f3e]/20'
                      : isAdditionalAvailable(additional)
                        ? 'bg-[#faf2e7] text-[#5c3a1f] border-[#e8d5c4] hover:border-[#8b6f3e]'
                        : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed',
                  ]"
                  :disabled="!isAdditionalAvailable(additional)"
                  @click="selectAdditional(additional)"
                >
                  <span class="flex-1 font-semibold">{{
                    additional.name
                  }}</span>
                  <span
                    class="font-bold text-xs"
                    :class="[
                      selectedAdditional?.id === additional.id
                        ? 'text-[#fff7e8]'
                        : isAdditionalAvailable(additional)
                          ? 'text-[#8b6f3e]'
                          : 'text-gray-400',
                    ]"
                  >
                    <span v-if="isAdditionalAvailable(additional)">+Rp {{ formatPrice(additional.price) }}</span>
                    <span v-else>(Habis)</span>
                  </span>
                </button>
              </div>
            </div>

            <!-- Quantity Selection -->
            <div class="mb-2">
              <div
                class="flex items-center justify-between bg-[#faf2e7] p-3 rounded-xl border border-[#e8d5c4]"
              >
                <label class="text-sm font-bold text-[#5c3a1f]">
                  Jumlah Pesanan
                </label>

                <div class="flex items-center gap-3">
                  <button
                    class="w-9 h-9 flex items-center justify-center rounded-lg bg-[#fff7e8] border border-[#f5e6d3] text-[#8b6f3e] font-bold shadow-sm active:bg-[#fffaf0] disabled:opacity-40"
                    :disabled="quantity <= 1"
                    @click="decreaseQuantity"
                  >
                    <span class="text-lg">−</span>
                  </button>

                  <span
                    class="w-8 text-center font-bold text-[#5c3a1f] text-sm sm:text-base"
                  >
                    {{ quantity }}
                  </span>

                  <button
                    class="w-9 h-9 flex items-center justify-center rounded-lg bg-[#fff7e8] border border-[#f5e6d3] text-[#8b6f3e] font-bold shadow-sm active:bg-[#fffaf0] disabled:opacity-40"
                    :disabled="quantity >= 99"
                    @click="increaseQuantity"
                  >
                    <span class="text-lg">+</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer with Add to Cart Button -->
          <div
            class="sticky bottom-0 bg-[#ffffff] border-t border-[#e8d5c4] px-4 py-4 shadow-lg"
          >
            <button
              class="w-full py-3.5 rounded-xl font-bold text-base transition-all active:scale-[0.97]"
              :class="[
                canAddToCart
                  ? 'bg-[#8b6f3e] text-white shadow-md'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed',
              ]"
              :disabled="!canAddToCart"
              @click="addToCart"
            >
              <span v-if="product.isOutOfStock">Stok Habis</span>
              <span v-else-if="variants.length > 0 && !selectedVariant">Pilih Varian</span>
              <span v-else>Tambah Ke Keranjang • Rp {{ formatPrice(totalPrice) }}</span>
            </button>
          </div>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script>
export default {
  name: "VariantModal",
  props: {
    isOpen: { type: Boolean, default: false },
    product: { type: Object, default: () => ({}) },
    variants: { type: Array, default: () => [] },
    additionals: { type: Array, default: () => [] }, // ⭐ NEW: Pass additionals from parent
  },
  emits: ["close", "add-to-cart"],
  data() {
    return {
      selectedVariant: null,
      selectedAdditional: null, // ⭐ NEW: Track selected additional
      quantity: 1,
      imageError: false,
    };
  },
  computed: {
    // ⭐ Check if additionals exist
    hasAdditionals() {
      return this.additionals && this.additionals.length > 0;
    },

    // ⭐ UPDATED: Calculate price with variant and additional
    currentPrice() {
      let price = this.product.price || 0;

      // If variant selected and has price, use variant price
      if (this.selectedVariant && this.selectedVariant.price) {
        price = this.selectedVariant.price;
      }

      // Add additional price if selected
      if (this.selectedAdditional && this.selectedAdditional.price) {
        price += this.selectedAdditional.price;
      }

      return price;
    },

    totalPrice() {
      return this.currentPrice * this.quantity;
    },

    canAddToCart() {
      if (this.product.isOutOfStock) return false;
      if (this.variants.length > 0 && !this.selectedVariant) return false;
      return this.quantity > 0;
    },
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.resetModal();
        document.body.style.overflow = "hidden";

        // ⭐ DEBUG: Log data when modal opens
        console.log("🔍 VariantModal opened with data:", {
          product: this.product,
          variants: this.variants,
          additionals: this.additionals,
        });
      } else {
        document.body.style.overflow = "";
      }
    },
  },
  methods: {
    closeModal() {
      this.$emit("close");
    },

    // ⭐ Check variant availability (handle different field names)
    isVariantAvailable(variant) {
      // Handle both is_available and isAvailable field names
      if (variant.is_available !== undefined) {
        return variant.is_available === true || variant.is_available === 1;
      }
      if (variant.isAvailable !== undefined) {
        return variant.isAvailable === true || variant.isAvailable === 1;
      }
      // Default to true if field not found
      return true;
    },

    selectVariant(variant) {
      if (!this.isVariantAvailable(variant)) return;
      this.selectedVariant = variant;
    },

    // ⭐ Check additional availability (handle different field names)
    isAdditionalAvailable(additional) {
      // Handle both is_available and isAvailable field names
      if (additional.is_available !== undefined) {
        return (
          additional.is_available === true || additional.is_available === 1
        );
      }
      if (additional.isAvailable !== undefined) {
        return additional.isAvailable === true || additional.isAvailable === 1;
      }
      // Default to true if field not found
      return true;
    },

    // ⭐ Select additional (toggle behavior)
    selectAdditional(additional) {
      if (!this.isAdditionalAvailable(additional)) return;

      // Toggle: if same additional clicked, deselect
      if (this.selectedAdditional?.id === additional.id) {
        this.selectedAdditional = null;
      } else {
        this.selectedAdditional = additional;
      }
    },

    increaseQuantity() {
      if (this.quantity < 99) this.quantity++;
    },

    decreaseQuantity() {
      if (this.quantity > 1) this.quantity--;
    },

    // ⭐ UPDATED: Pass additional data to cart
    addToCart() {
      if (!this.canAddToCart) return;

      const cartItem = {
        ...this.product,
        varian: this.selectedVariant,
        quantity: this.quantity,
        basePrice: this.product.price, // ⭐ Store original price
        selectedAdditionalId: this.selectedAdditional
          ? this.selectedAdditional.id
          : null, // ⭐ NEW
        additionals: this.additionals || [], // ⭐ NEW: Pass all additionals for reference
        isLocked: !!this.selectedAdditional, // ⭐ NEW: Lock if additional selected
      };

      console.log("🛒 Adding to cart:", cartItem);
      this.$emit("add-to-cart", cartItem);
      this.closeModal();
    },

    formatPrice(val) {
      return val.toLocaleString("id-ID");
    },

    // ⭐ Reset modal state
    resetModal() {
      this.selectedVariant = null;
      this.selectedAdditional = null;
      this.quantity = 1;
      this.imageError = false;
    },
  },
};
</script>

<style scoped>
.modal-backdrop-enter-active,
.modal-backdrop-leave-active {
  transition: opacity 0.4s ease;
}
.modal-backdrop-enter-from,
.modal-backdrop-leave-to {
  opacity: 0;
}
.modal-slide-enter-active {
  transition: transform 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}
.modal-slide-leave-active {
  transition: transform 0.4s ease-in;
}
.modal-slide-enter-from,
.modal-slide-leave-to {
  transform: translateY(100%);
}
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
